import { useState } from "react";
import { useTranslation } from "react-i18next";
import { convertText } from "@/utils/langConvert";
import { motion } from "framer-motion";
import { funTools } from "../data";
import { track } from "@/utils/track";
import { comingSoonToastPortal } from "./comingSoonToast";

interface FunToolsGridProps {
  isLight: boolean;
  onOpenTool?: (toolId: string) => boolean;
  availableToolIds?: string[];
}

export default function FunToolsGrid({ isLight, onOpenTool, availableToolIds = [] }: FunToolsGridProps) {
  const { i18n } = useTranslation();
  const trd = (text: string) => convertText(text, i18n.language);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [comingSoonToast, setComingSoonToast] = useState(false);

  const handleToolClick = (toolId: string) => {
    track("tool_use", { toolId });
    if (onOpenTool?.(toolId)) {
      return;
    }
    setComingSoonToast(true);
    setTimeout(() => setComingSoonToast(false), 2500);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="w-full max-w-4xl mx-auto"
    >
      {/* Coming Soon Toast — rendered to body to avoid being clipped by parent transforms */}
      {comingSoonToastPortal({ isLight, show: comingSoonToast, trd })}

      {/* Header */}
      <div className="text-center mb-8">
        <h2
          className="text-2xl font-bold mb-1"
          style={{ color: "var(--text-primary)" }}
        >
          {trd("占卜问事")}
        </h2>
        <p
          className="text-sm"
          style={{ color: "var(--text-secondary)" }}
        >
          {trd("八種占卜工具，輕鬆探索命理世界")}
        </p>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {funTools.map((tool) => {
          const isAvailable = availableToolIds.includes(tool.id);
          return (
            <motion.div
              key={tool.id}
              whileHover={isAvailable ? { y: -4 } : undefined}
              transition={{ duration: 0.2 }}
              onMouseEnter={() => setHoveredId(tool.id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => handleToolClick(tool.id)}
              className={`relative rounded-xl overflow-hidden border cursor-pointer group ${
                !isAvailable ? "opacity-70" : ""
              }`}
              style={{
                background: "var(--bg-card)",
                borderColor: "var(--border-color)",
              }}
            >
              {/* Background image */}
              <div
                className="absolute inset-0 bg-cover bg-center opacity-30 group-hover:opacity-40 transition-opacity duration-300"
                style={{ backgroundImage: `url('${tool.bgImage}')` }}
              />
              <div
                className="absolute inset-0 transition-colors duration-300"
                style={{
                  background: isLight
                    ? hoveredId === tool.id
                      ? "linear-gradient(180deg, rgba(245,245,247,0.72), rgba(245,245,247,0.9))"
                      : "linear-gradient(180deg, rgba(245,245,247,0.82), rgba(245,245,247,0.96))"
                    : hoveredId === tool.id
                      ? "linear-gradient(180deg, rgba(42,26,58,0.72), rgba(26,15,46,0.9))"
                      : "linear-gradient(180deg, rgba(42,26,58,0.82), rgba(26,15,46,0.96))",
                }}
              />

              {/* Lock badge for unavailable tools */}
              {!isAvailable && (
                <div
                  className={`absolute top-2 right-2 z-10 w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                    isLight ? "bg-[#e5e5ea] text-[#8e8e93]" : "bg-white/10 text-silver-400"
                  }`}
                >
                  <i className="ri-lock-line" />
                </div>
              )}

              {/* Content */}
              <div className="relative p-5 md:p-6 flex flex-col items-center text-center h-full min-h-[140px] md:min-h-[160px]">
                {/* Icon */}
                <div
                  className="w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center mb-3 transition-colors duration-300"
                  style={{
                    background: hoveredId === tool.id ? "var(--accent-primary-20)" : "var(--bg-track)",
                  }}
                >
                  <i
                    className={`${tool.icon} text-xl md:text-2xl transition-colors duration-300`}
                    style={{ color: hoveredId === tool.id ? "var(--accent-primary)" : "var(--text-secondary)" }}
                  />
                </div>

                {/* Name */}
                <h3
                  className="text-base md:text-lg font-bold mb-1"
                  style={{ color: "var(--text-primary)" }}
                >
                  {trd(tool.name)}
                </h3>

                {/* Desc */}
                <p
                  className="text-xs leading-relaxed"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {trd(tool.desc)}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Bottom hint */}
      <p
        className="text-center text-xs mt-6"
        style={{ color: "var(--text-tertiary, var(--text-secondary))" }}
      >
        {trd("下载 APP 解锁完整功能体验")}
      </p>
    </motion.div>
  );
}
